/**
 * VÉRAULT TRAVELS — Route Planner Component
 * 
 * Calculate distance and travel time between locations.
 * Powered by OSRM (free, no key required).
 */

import React, { useState } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import {
  Navigation, MapPin, Clock, Loader2, ArrowRight, Plus, Trash2,
  Car, PersonStanding, ChevronDown, ChevronUp
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function RoutePlanner({ destinations }) {
  const [stops, setStops] = useState(
    destinations?.length >= 2
      ? destinations.slice(0, 2).map(d => d.name || d.destination || '')
      : ['', '']
  );
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState('driving');
  const [expanded, setExpanded] = useState(true);

  const geocode = async (place) => {
    try {
      const res = await axios.get('https://geocoding-api.open-meteo.com/v1/search', {
        params: { name: place, count: 1, language: 'en' },
        timeout: 10000,
      });
      if (res.data?.results?.[0]) {
        return { lat: res.data.results[0].latitude, lon: res.data.results[0].longitude };
      }
    } catch (e) {}
    return null;
  };

  const calculateRoutes = async () => {
    const validStops = stops.filter(s => s.trim());
    if (validStops.length < 2) {
      toast.error('Enter at least 2 locations');
      return;
    }

    setLoading(true);
    try {
      // Geocode all stops
      const coords = await Promise.all(validStops.map(s => geocode(s)));
      const validCoords = coords.filter(Boolean);
      if (validCoords.length < 2) {
        toast.error('Could not find one or more locations');
        setLoading(false);
        return;
      }

      // Calculate routes between consecutive pairs
      const results = [];
      for (let i = 0; i < validCoords.length - 1; i++) {
        const res = await axios.get(`${API}/directions/route`, {
          params: {
            origin_lat: validCoords[i].lat,
            origin_lon: validCoords[i].lon,
            dest_lat: validCoords[i + 1].lat,
            dest_lon: validCoords[i + 1].lon,
            mode,
          },
        });
        if (res.data.routes?.length > 0) {
          results.push({
            from: validStops[i],
            to: validStops[i + 1],
            ...res.data.routes[0],
          });
        }
      }

      setRoutes(results);
      if (results.length === 0) {
        toast.error('No routes found');
      }
    } catch (e) {
      toast.error('Route calculation failed');
    } finally {
      setLoading(false);
    }
  };

  const addStop = () => setStops([...stops, '']);
  const removeStop = (idx) => {
    if (stops.length <= 2) return;
    setStops(stops.filter((_, i) => i !== idx));
  };

  const totalDistance = routes.reduce((sum, r) => sum + (r.distance_km || 0), 0);
  const totalDuration = routes.reduce((sum, r) => sum + (r.duration_s || 0), 0);

  const formatDuration = (sec) => {
    if (!sec) return '--';
    const min = Math.round(sec / 60);
    if (min < 60) return `${min} min`;
    const hr = Math.floor(min / 60);
    const rem = min % 60;
    return rem ? `${hr} hr ${rem} min` : `${hr} hr`;
  };

  return (
    <div className="bg-white rounded-xl border border-[rgba(0,0,0,0.06)] p-6" data-testid="route-planner">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center justify-between mb-4"
      >
        <h3 className="font-medium text-[#1A1A1A] flex items-center gap-2">
          <Navigation className="w-5 h-5 text-[#C8A85A]" />
          Route Planner
        </h3>
        {expanded ? <ChevronUp className="w-4 h-4 text-[#8C8C8C]" /> : <ChevronDown className="w-4 h-4 text-[#8C8C8C]" />}
      </button>

      {expanded && (
        <div className="space-y-4">
          <p className="text-sm text-[#8C8C8C]">
            Calculate distance and travel time between locations. Powered by OSRM.
          </p>

          {/* Mode Toggle */}
          <div className="flex gap-2">
            {[
              { id: 'driving', label: 'Drive', icon: Car },
              { id: 'walking', label: 'Walk', icon: PersonStanding },
            ].map(m => (
              <button
                key={m.id}
                onClick={() => setMode(m.id)}
                className={`px-3 py-1.5 text-xs rounded-lg flex items-center gap-1.5 transition-colors ${
                  mode === m.id
                    ? 'bg-[#C8A85A] text-white'
                    : 'bg-[#F5F1EB] text-[#595959] hover:bg-[#EEE9E2]'
                }`}
                data-testid={`route-mode-${m.id}`}
              >
                <m.icon className="w-3.5 h-3.5" />
                {m.label}
              </button>
            ))}
          </div>

          {/* Stop Inputs */}
          <div className="space-y-2">
            {stops.map((stop, idx) => (
              <div key={idx} className="flex items-center gap-2">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 ${
                  idx === 0 ? 'bg-[#C8A85A] text-white' : idx === stops.length - 1 ? 'bg-[#8DA399] text-white' : 'bg-[#EEE9E2] text-[#595959]'
                }`}>
                  {idx + 1}
                </div>
                <div className="relative flex-1">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#8C8C8C]" />
                  <Input
                    value={stop}
                    onChange={(e) => {
                      const next = [...stops];
                      next[idx] = e.target.value;
                      setStops(next);
                    }}
                    placeholder={idx === 0 ? 'Starting point...' : idx === stops.length - 1 ? 'Destination...' : 'Stop...'}
                    className="pl-9 bg-[#F5F1EB] h-9 text-sm"
                    onKeyDown={(e) => { if (e.key === 'Enter') calculateRoutes(); }}
                    data-testid={`route-stop-${idx}`}
                  />
                </div>
                {stops.length > 2 && (
                  <button onClick={() => removeStop(idx)} className="p-1 hover:bg-red-50 rounded">
                    <Trash2 className="w-3.5 h-3.5 text-red-400" />
                  </button>
                )}
              </div>
            ))}
          </div>

          <div className="flex gap-2">
            <Button
              onClick={addStop}
              variant="ghost"
              size="sm"
              className="text-xs text-[#8C8C8C]"
            >
              <Plus className="w-3.5 h-3.5 mr-1" /> Add Stop
            </Button>
            <Button
              onClick={calculateRoutes}
              disabled={loading}
              size="sm"
              className="bg-[#C8A85A] hover:bg-[#B39049] text-white ml-auto"
              data-testid="calculate-route-btn"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Navigation className="w-4 h-4 mr-1" />}
              Calculate
            </Button>
          </div>

          {/* Route Results */}
          {routes.length > 0 && (
            <div className="space-y-2 pt-2 border-t border-[rgba(0,0,0,0.06)]">
              {routes.map((route, idx) => (
                <div key={idx} className="flex items-center gap-3 p-3 bg-[#F9F8F4] rounded-lg" data-testid={`route-result-${idx}`}>
                  <div className="flex items-center gap-1 flex-1 min-w-0">
                    <span className="text-sm font-medium text-[#1A1A1A] truncate">{route.from}</span>
                    <ArrowRight className="w-4 h-4 text-[#C8A85A] flex-shrink-0" />
                    <span className="text-sm font-medium text-[#1A1A1A] truncate">{route.to}</span>
                  </div>
                  <div className="flex items-center gap-4 flex-shrink-0">
                    <span className="text-sm text-[#595959] flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5 text-[#C8A85A]" />
                      {route.duration_text}
                    </span>
                    <span className="text-sm text-[#8C8C8C]">
                      {route.distance_text}
                    </span>
                  </div>
                </div>
              ))}

              {/* Total Summary */}
              {routes.length > 1 && (
                <div className="flex items-center justify-between p-3 bg-[#C8A85A]/10 rounded-lg">
                  <span className="text-sm font-medium text-[#1A1A1A]">Total Trip</span>
                  <div className="flex items-center gap-4">
                    <span className="text-sm font-medium text-[#C8A85A]">{formatDuration(totalDuration)}</span>
                    <span className="text-sm text-[#595959]">{totalDistance.toFixed(1)} km</span>
                  </div>
                </div>
              )}

              <p className="text-xs text-[#B0B0B0]">Source: OSRM (Live routing data)</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default RoutePlanner;
